import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Info, Hammer, Pencil, Users, LogOut, Bell, Menu, X, Receipt, Sun, Moon, Package, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = '';

// Notification sound (base64 encoded short beep sound)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp2XjYJ4bWVgY21+jpugnpiNg3htY15oc4SNm56alomBd21mYmhzgYyXmpePh3xzbmhja3WBi5aZlY+He3NsaGVpdoGLlZiUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZiUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZiUj4d7c21pZWl2gYuVmJSPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZiUj4d7c2xpZWl2gYuVl5SPh3tzbWllaXaBi5WYlI+He3NsaWVpdoGLlZeUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WXlI+He3NsaWVpdoGLlZiUj4d7c2xpZWl2gYuVl5SPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZeUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WXlI+He3NsaWVpdoGLlZiUj4d7c21pZWl2gYuVl5SPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZeUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WXlI+He3NsaWVpdoGLlZiUj4d7c2xpZWl2gYuVl5SPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZeUj4d7c21pZWl2gYuVmJSPh3tzbGllaXaBi5WXlI+He3NsaWVpdoGLlZiUj4d7c2xpZWl2gYuVl5SPh3tzbGllaXaBi5WYlI+He3NsaWVpdoGLlZeUj4d7c2xpZWl2gYuVmJSPh3tzbGllaXaBi5WYlI+He3Nsag==';

export default function Layout({ children, user, onLogout, theme, onThemeToggle }) {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationRef = useRef(null);
  const mobileNotificationRef = useRef(null);
  const previousUnreadCount = useRef(0);
  const audioRef = useRef(null);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND);
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close sidebar when route changes (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Auto-hide notification on mouse leave
  useEffect(() => {
    const handleMouseLeave = (e) => {
      // Check if mouse left the notification popup area
      if (showNotifications) {
        const desktopNotif = notificationRef.current;
        const mobileNotif = mobileNotificationRef.current;
        
        if (desktopNotif && !desktopNotif.contains(e.relatedTarget)) {
          setShowNotifications(false);
        }
        if (mobileNotif && !mobileNotif.contains(e.relatedTarget)) {
          setShowNotifications(false);
        }
      }
    };

    // Add mouseleave event to notification popups
    const desktopNotif = notificationRef.current;
    const mobileNotif = mobileNotificationRef.current;
    
    if (desktopNotif) {
      desktopNotif.addEventListener('mouseleave', handleMouseLeave);
    }
    if (mobileNotif) {
      mobileNotif.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (desktopNotif) {
        desktopNotif.removeEventListener('mouseleave', handleMouseLeave);
      }
      if (mobileNotif) {
        mobileNotif.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [showNotifications]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications/unread-count`);
      const newCount = response.data.count;
      
      // Play sound if new notifications arrived
      if (newCount > previousUnreadCount.current && previousUnreadCount.current !== 0) {
        playNotificationSound();
      }
      
      previousUnreadCount.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleNotificationClick = async () => {
    setShowNotifications(!showNotifications);
    if (!showNotifications) {
      fetchNotifications();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-read`);
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read');
    }
  };

  const navItems = [
    { path: '/dashboard', key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/about', key: 'about', label: 'About', icon: Info },
    { path: '/design', key: 'design', label: 'Design Dept', icon: Pencil },
    { path: '/construction', key: 'construction', label: 'Construction', icon: Hammer },
    { path: '/admin', key: 'admin', label: 'Admin Dept', icon: Users },
    { path: '/accounting', key: 'finance', label: 'Finance', icon: Receipt },
    { path: '/supply-chain', key: 'supply-chain', label: 'Supply Chain', icon: Package },
    { path: '/employee-hub', key: 'employee-hub', label: 'Employee Hub', icon: Users },
  ];

  const perms = user.permissions || [];
  const visibleNav = user.role === 'superadmin'
    ? [{ path: '/admin-console', label: 'Admin Console', icon: ShieldCheck }]
    : navItems.filter(item => perms.includes(item.key));

  return (
    <div className="min-h-screen bg-background grid-background flex flex-col md:flex-row">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b border-border/40 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-muted rounded-md"
            data-testid="btn-mobile-menu"
          >
            <Menu className="w-6 h-6 text-foreground" />
          </button>
          <div className="flex items-center gap-2">
            <img 
              src="/desq-logo.png" 
              alt="Desq Logo" 
              className="w-8 h-8 object-contain"
            />
            <span className="text-lg font-bold text-foreground">Desq</span>
          </div>
        </div>
        
        {/* Mobile Theme Toggle & Notification Bell */}
        <div className="flex items-center gap-1">
          <button
            onClick={onThemeToggle}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            data-testid="btn-mobile-theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-500" />}
          </button>
          {['admin', 'principal', 'hr', 'project_manager', 'design_lead', 'design'].includes(user.role) && (
          <button
            onClick={handleNotificationClick}
            className="p-2 hover:bg-muted rounded-md relative"
            data-testid="btn-mobile-notifications"
          >
            <Bell className="w-5 h-5 text-foreground" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
          )}
        </div>
      </header>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed md:static inset-y-0 left-0 z-50
          w-64 bg-card border-r border-border/40 flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-border/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/desq-logo.png" 
              alt="Desq Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold text-foreground">Desq</h1>
              <p className="text-xs text-muted-foreground">Employee Portal</p>
            </div>
          </div>
          {/* Close button for mobile */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-2 hover:bg-muted rounded-md"
            data-testid="btn-close-sidebar"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <nav className="flex-1 p-4 sidebar-nav space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-primary/10 text-primary border-l-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-border/40">
          {/* Notification Bell - Desktop only */}
          {['admin', 'principal', 'hr', 'project_manager', 'design_lead', 'design'].includes(user.role) && (
            <div className="relative mb-3 hidden md:block">
              <Button
                variant="ghost"
                className="w-full justify-start relative"
                onClick={handleNotificationClick}
                data-testid="btn-notifications"
              >
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {unreadCount > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-accent text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {showNotifications && (
                <div 
                  ref={notificationRef}
                  className="absolute bottom-full left-0 w-80 mb-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
                  onMouseLeave={() => setShowNotifications(false)}
                >
                  <div className="p-3 border-b border-border flex items-center justify-between">
                    <span className="font-medium text-sm">Notifications</span>
                    {unreadCount > 0 && (
                      <button
                        onClick={handleMarkAllRead}
                        className="text-xs text-primary hover:underline"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="divide-y divide-border/40">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 10).map((notif) => (
                        <div
                          key={notif.id}
                          className={`p-3 text-sm ${!notif.is_read ? 'bg-primary/5' : ''}`}
                        >
                          <p className="font-medium text-foreground">{notif.title}</p>
                          <p className="text-xs text-muted-foreground">{notif.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notif.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <div className="mb-3 px-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">{user.full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role} · {user.department}</p>
            </div>
            <button
              onClick={onThemeToggle}
              className="p-2 hover:bg-muted rounded-md transition-colors hidden md:flex"
              data-testid="btn-desktop-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-500" />}
            </button>
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={onLogout}
            data-testid="btn-logout"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 pb-24">
        {children}
      </main>

      {/* Mobile Notifications Dropdown */}
      {showNotifications && (
        <div 
          ref={mobileNotificationRef}
          className="md:hidden fixed top-14 right-2 w-80 max-w-[calc(100vw-1rem)] bg-card border border-border rounded-lg shadow-lg z-50 max-h-[60vh] overflow-y-auto"
          onMouseLeave={() => setShowNotifications(false)}
        >
          <div className="p-3 border-b border-border flex items-center justify-between">
            <span className="font-medium text-sm">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary hover:underline"
              >
                Mark all read
              </button>
            )}
          </div>
          <div className="divide-y divide-border/40">
            {notifications.length > 0 ? (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 text-sm ${!notif.is_read ? 'bg-primary/5' : ''}`}
                >
                  <p className="font-medium text-foreground">{notif.title}</p>
                  <p className="text-xs text-muted-foreground">{notif.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(notif.created_at).toLocaleString()}
                  </p>
                </div>
              ))
            ) : (
              <p className="p-4 text-sm text-muted-foreground text-center">No notifications</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
